/**
 * useBrainState — the diagnosis panel's state, fed by tse:diag_state and tse:command_result (plan 15.3, F2).
 *
 * Stores the latest diag_state per session_id with (diagnosis_id, seq) ordering (brainStateLogic.js), so a
 * stale or replayed event never rewinds the panel. The panel holds no state of its own; commands are
 * fire-and-wait: `markCommandPending(name)` until the matching tse:command_result arrives.
 *
 * Returns {diagState, diagnosisCount, pendingCommands, lastCommandResult, applyDiagState,
 *          applyCommandResult, markCommandPending, clearCommandPending, reset}.
 */
import { useCallback, useMemo, useRef, useState } from "react";

import { DECISION, decideDiagState, nextEntry } from "./brainStateLogic";

const EMPTY = Object.freeze({});

export default function useBrainState(sessionId) {
  const [entries, setEntries] = useState(EMPTY);
  const entriesRef = useRef(EMPTY);
  const [pendingCommands, setPendingCommands] = useState(EMPTY);
  const [lastCommandResult, setLastCommandResult] = useState(null);

  const commit = useCallback((next) => {
    entriesRef.current = next;
    setEntries(next);
  }, []);

  /** Handler for tse:diag_state. Returns the decision (useful to callers and tests). */
  const applyDiagState = useCallback((payload) => {
    const sid = payload?.session_id;
    const entry = sid ? entriesRef.current[sid] : undefined;
    const decision = decideDiagState(entry, payload);
    if (decision === DECISION.ACCEPT || decision === DECISION.NEW_DIAGNOSIS) {
      commit({ ...entriesRef.current, [sid]: nextEntry(entry, payload, decision) });
    }
    return decision;
  }, [commit]);

  const markCommandPending = useCallback((command) => {
    if (!command) return;
    setPendingCommands((prev) => ({ ...prev, [command]: true }));
  }, []);

  /** Drops one pending flag with no result (a reply that never came). */
  const clearCommandPending = useCallback((command) => {
    setPendingCommands((prev) => {
      if (!prev[command]) return prev;
      const next = { ...prev };
      delete next[command];
      return next;
    });
  }, []);

  /** Handler for tse:command_result: any status clears the pending flag; an ignored command is never retried. */
  const applyCommandResult = useCallback((payload) => {
    if (!payload || typeof payload.command !== "string") return;
    setLastCommandResult(payload);
    clearCommandPending(payload.command);
  }, [clearCommandPending]);

  /** New session or clear: forget every stored diagnosis (the popover hides and resets). */
  const reset = useCallback(() => {
    commit(EMPTY);
    setPendingCommands(EMPTY);
    setLastCommandResult(null);
  }, [commit]);

  const entry = sessionId ? entries[sessionId] : undefined;
  return useMemo(() => ({
    diagState: entry?.state || null,
    diagnosisCount: entry?.diagnosisCount || 0,
    pendingCommands,
    lastCommandResult,
    applyDiagState,
    applyCommandResult,
    markCommandPending,
    clearCommandPending,
    reset,
  }), [entry, pendingCommands, lastCommandResult, applyDiagState, applyCommandResult, markCommandPending, clearCommandPending, reset]);
}
